import { useCallback, useEffect, useState } from 'react';

type MenuComponent = typeof import('./LanguagePillMenu').default;

/**
 * Loads the language dropdown (Radix menu + floating-ui, ~25 KB gzipped) off the critical path:
 * once the page has finished loading and the browser is idle, or immediately when the user
 * points at / focuses / clicks the placeholder button. `clickedEarly` tells the menu to open
 * straight away when the click happened before it arrived.
 *
 * Deliberately not React.lazy/Suspense: React 18 delays committing a tree that contains a new
 * Suspense fallback by up to 500 ms when another fallback was shown just before (the route's).
 */
export function useLanguageMenu(enabled: boolean) {
  const [Menu, setMenu] = useState<MenuComponent | null>(null);
  const [clickedEarly, setClickedEarly] = useState(false);

  const load = useCallback(() => {
    void import('./LanguagePillMenu').then((m) => setMenu(() => m.default));
  }, []);

  useEffect(() => {
    if (!enabled || Menu) return;
    let idleHandle: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleHandle = window.requestIdleCallback(load, { timeout: 5000 });
      } else {
        timer = setTimeout(load, 3000);
      }
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => {
      window.removeEventListener('load', schedule);
      if (idleHandle !== undefined) window.cancelIdleCallback(idleHandle);
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [enabled, Menu, load]);

  const placeholderProps = {
    onClick: () => {
      setClickedEarly(true);
      load();
    },
    onPointerEnter: load,
    onFocus: load,
    onTouchStart: load,
  };

  return { Menu, clickedEarly, placeholderProps };
}
